/* Premier Pop Art — "PPABrowse" (V11_PORT_INVENTORY.md #23).
 *
 * A budget filter (All / Under $2,500 / $2,500-$10,000 / Over $10,000 / Price on
 * request) and a true price sort, both computed against the *listed* price —
 * the real cart price when one exists, otherwise the numeric value stored in
 * metafields.artwork.gallery_value for the ~1,000 works priced at $0. Real
 * Shopify price sort/filter can't reason about that, so whenever either is
 * active we fetch the whole collection as JSON (?view=ppa-browse-index-v2,
 * 250 products/page, concurrent batches) and filter/sort/render it ourselves.
 *
 * Coexistence with Prestige 11's native facets/sort (documented decision):
 * Prestige 11 answers a filter or sort change by dispatching a bubbling
 * "facet:update" CustomEvent (from <facets-form>, <facets-sort-popover> or
 * <facet-link>) that a listener on `document` uses to fetch just that section's
 * HTML and swap it in place (see js/common/facets/facets-listeners.js in
 * theme.js) — no full page load. That swap would silently drop our
 * `ppa_budget` param and re-run price sort against the real ($0) variant price.
 * The live Prestige 5 theme guarded the equivalent race by intercepting the
 * native filter form in the capture phase and forcing a real navigation
 * instead of letting the native AJAX run — we make the same choice here for
 * the same reason: every native facet/sort change is caught on `document` in
 * the capture phase (`stopImmediatePropagation`), our own `ppa_budget` is
 * merged into the destination URL, and the browser navigates there for real
 * — deterministic result, no stale-DOM race, and one navigation state
 * machine to reason about. It does mean Prestige 11's fast in-place filter
 * swap doesn't apply on this theme, everywhere PPABrowse is present, i.e.
 * every collection page — a deliberate tradeoff, not an oversight.
 */
(() => {
  "use strict";
  if (customElements.get("ppa-browse")) return;

  function listedPrice(product) {
    if (Number.isSafeInteger(product.price) && product.price > 0) return product.price;
    const value = String(product.gallery || "")
      .trim()
      .replace(/^USD\s*/i, "")
      .replace(/\s*USD$/i, "")
      .replace(/^\$\s*/, "");
    // Reject ambiguous ranges and prose instead of turning them into cheap works.
    if (!/^(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d{1,2})?$/.test(value)) return null;
    const cents = Math.round(Number(value.replace(/,/g, "")) * 100);
    return cents > 0 && Number.isSafeInteger(cents) ? cents : null;
  }

  function inBudget(product, budget) {
    const price = product.listed;
    if (budget === "all") return true;
    // Sold works have their own band and never appear in a price band.
    if (budget === "sold") return product.sold_tag === true;
    if (product.sold_tag === true) return false;
    if (budget === "on-request") return price === null;
    if (price === null) return false;
    if (budget === "under-2500") return price < 250000;
    if (budget === "2500-10000") return price >= 250000 && price <= 1000000;
    if (budget === "over-10000") return price > 1000000;
    return false;
  }

  class PPABrowse extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.budget = this.querySelector("[data-budget]");
      this.sort = this.querySelector("[data-sort]");
      this.status = this.querySelector("[data-status]");
      this.cache = new Map();

      const url = new URL(location.href);
      this.budget.value = url.searchParams.get("ppa_budget") || "all";
      if (!this.budget.value) this.budget.value = "all";
      this.sort.value = url.searchParams.get("sort_by") || this.dataset.defaultSort;

      this.budget.addEventListener("change", () => this.change());
      this.sort.addEventListener("change", () => this.change());

      this.querySelector("[data-reset]").addEventListener("click", () => {
        const next = new URL(location.href);
        for (const key of [...next.searchParams.keys()]) {
          if (key.startsWith("filter.") || key.startsWith("ppa_") || key === "page" || key === "sort_by")
            next.searchParams.delete(key);
        }
        location.assign(next);
      });

      // See the file header: every native facet/sort change is turned into a
      // real navigation that carries our own budget param along with it — but
      // only while a budget filter or a price sort is actually active. With
      // no budget filter and no price sort, our own state doesn't need to
      // survive the swap and the real Shopify price is fine to sort on, so
      // there is nothing for us to protect: leave the event alone and let
      // Prestige 11's native AJAX (including its own pagination links, which
      // are `facet-link`s that dispatch this same event) run untouched.
      this.facetUpdateHandler = (event) => {
        if (!this.needsIndex()) return;
        event.stopImmediatePropagation();
        const url = new URL(event.detail.url, location.origin);
        url.searchParams.delete("section_id");
        url.searchParams.delete("page");
        url.searchParams.delete("ppa_page");
        if (this.budget.value === "all") url.searchParams.delete("ppa_budget");
        else url.searchParams.set("ppa_budget", this.budget.value);
        location.assign(url);
      };
      document.addEventListener("facet:update", this.facetUpdateHandler, true);

      this.popHandler = () => location.reload();
      window.addEventListener("popstate", this.popHandler);

      if (this.needsIndex()) this.update();
    }

    disconnectedCallback() {
      this.abort?.abort();
      document.removeEventListener("facet:update", this.facetUpdateHandler, true);
      window.removeEventListener("popstate", this.popHandler);
    }

    needsIndex() {
      return this.budget.value !== "all" || this.sort.value.startsWith("price-");
    }

    change() {
      const url = new URL(location.href);
      url.searchParams.delete("page");
      url.searchParams.delete("ppa_page");
      url.searchParams.delete("filter.v.price.gte");
      url.searchParams.delete("filter.v.price.lte");
      if (this.budget.value === "all") url.searchParams.delete("ppa_budget");
      else url.searchParams.set("ppa_budget", this.budget.value);
      url.searchParams.set("sort_by", this.sort.value);
      if (!this.needsIndex()) {
        location.assign(url);
        return;
      }
      history.pushState({}, "", url);
      this.update();
    }

    async loadIndex(signal) {
      const url = new URL(this.dataset.collection, location.origin);
      const current = new URL(location.href);
      for (const [key, value] of current.searchParams) {
        if ((key.startsWith("filter.") && !key.startsWith("filter.v.price.")) || key === "q" || key === "preview_theme_id")
          url.searchParams.append(key, value);
      }
      url.searchParams.set("sort_by", this.sort.value.startsWith("price-") ? "title-ascending" : this.sort.value);
      url.searchParams.set("view", "ppa-browse-index-v2");
      const key = url.toString();
      if (this.cache.has(key)) return this.cache.get(key);

      const getPage = async (page) => {
        const request = new URL(url);
        request.searchParams.set("page", String(page));
        const response = await fetch(request, { signal, credentials: "same-origin" });
        if (!response.ok) throw new Error("Catalog request failed");
        const data = await response.json();
        if (
          !Array.isArray(data.products) ||
          data.page !== page ||
          !Number.isSafeInteger(data.count) ||
          data.count < 0 ||
          !Number.isSafeInteger(data.pages) ||
          data.pages < 0 ||
          (data.count > 0 && data.pages < 1) ||
          data.products.length > 250
        )
          throw new Error("Invalid catalog page");
        if (data.currency !== "USD") throw new Error("USD browsing is unavailable in this currency");
        return data;
      };

      const first = await getPage(1);
      if (first.count > 25000 || first.pages > 100) throw new Error("Collection exceeds supported index size");
      const products = [...first.products];
      for (let page = 2; page <= first.pages; page += 6) {
        const batch = await Promise.all(
          Array.from({ length: Math.min(6, first.pages - page + 1) }, (_, i) => getPage(page + i)),
        );
        for (const part of batch) {
          if (part.count !== first.count || part.pages !== first.pages) throw new Error("Catalog changed during loading");
          products.push(...part.products);
          this.status.textContent = `Preparing prices… ${products.length.toLocaleString()} of ${first.count.toLocaleString()} works`;
        }
      }
      if (products.length !== first.count || new Set(products.map((p) => p.id)).size !== first.count)
        throw new Error("Incomplete catalog");

      const normalized = products.map((p) => ({ ...p, listed: listedPrice(p) }));
      this.cache.set(key, normalized);
      return normalized;
    }

    setResultCount(count) {
      const heading = this.querySelector('[data-result-count]');
      if (heading) heading.textContent = count === null ? 'Loading works…' : count === false ? 'Results unavailable' : `${count.toLocaleString()} ${count === 1 ? 'work' : 'works'}`;
    }

    async update() {
      this.abort?.abort();
      const controller = (this.abort = new AbortController());
      const main = document.querySelector(".collection__main");
      if (!main) return;
      main.setAttribute("aria-busy", "true");
      this.status.textContent = "Finding artwork across the collection…";
      this.setResultCount(null);
      try {
        const products = await this.loadIndex(controller.signal);
        if (controller.signal.aborted) return;
        this.filtered = products.filter((p) => inBudget(p, this.budget.value));
        if (this.sort.value.startsWith("price-")) {
          const direction = this.sort.value === "price-descending" ? -1 : 1;
          this.filtered.sort((a, b) => {
            if (a.listed === null && b.listed !== null) return 1;
            if (b.listed === null && a.listed !== null) return -1;
            return direction * ((a.listed || 0) - (b.listed || 0)) || a.title.localeCompare(b.title) || a.id - b.id;
          });
        }
        this.render(Number(new URL(location.href).searchParams.get("ppa_page")) || 1);
      } catch (error) {
        if (controller.signal.aborted) return;
        // Do not silently present a partial page as a complete filtered collection.
        const grid = main.querySelector("product-list.product-list");
        const pager = main.querySelector(".ppa-browse__pager");
        pager?.remove();
        const message = document.createElement("p");
        message.className = "ppa-browse__error";
        message.append(
          "We couldn’t load these results. Please try again or clear the filters to browse all artwork.",
          document.createElement("br"),
        );
        const retry = document.createElement("button");
        retry.type = "button";
        retry.className = "button button--secondary";
        retry.textContent = "Try again";
        retry.addEventListener("click", () => this.update());
        message.append(retry);
        if (grid) grid.replaceWith(message);
        else main.append(message);
        this.status.textContent = "Results unavailable.";
        this.setResultCount(false);
      } finally {
        if (!controller.signal.aborted) main.removeAttribute("aria-busy");
      }
    }

    card(product) {
      const el = (tag, className, text) => {
        const node = document.createElement(tag);
        if (className) node.className = className;
        if (text != null) node.textContent = text;
        return node;
      };
      const link = (className, text) => {
        const node = el("a", className, text);
        node.href = product.url;
        return node;
      };

      const aspect = (this.dataset.aspectRatio || "square").split("_")[0];
      const cropClass = (this.dataset.aspectRatio || "").includes("crop") ? " object-cover" : "";
      const textFont = this.dataset.textFont === "heading" ? "h6" : "";
      const showVendor = this.dataset.showVendor === "true";
      const showSoldOutBadge = this.dataset.showSoldOutBadge === "true";
      const maxLines = Number(this.dataset.titleMaxLines) || 0;

      const item = document.createElement("product-card");
      item.className = "product-card" + (product.inquiry === "true" ? " ppa-card--por" : "");
      item.setAttribute("handle", product.url.split("/").pop());

      // `display` is already-escaped HTML (rendered through ppa-artwork-title,
      // which runs its output through the `escape` filter) so a title like
      // "Marilyn's Dream" arrives here as "Marilyn&#39;s Dream". Decode it back
      // to plain text before handing it to textContent, which would otherwise
      // print the literal entities. DOMParser never executes scripts/styles
      // from the string it's given, so this stays safe against the safety
      // rule against unsanitised innerHTML (ARCHITECTURE.md Rule 4).
      const decodeEntities = (value) =>
        new DOMParser().parseFromString(String(value ?? ""), "text/html").documentElement.textContent;

      if (product.image) {
        const figure = el("div", "product-card__figure");
        item.append(figure);

        if (product.sold && showSoldOutBadge) {
          const badges = el("badge-list", "badge-list badge-list--vertical");
          badges.append(el("sold-out-badge", "badge badge--sold-out", "Sold out"));
          figure.append(badges);
        }

        const media = link("product-card__media");
        media.draggable = false;
        media.setAttribute("data-instant", "");
        const image = el("img", `product-card__image product-card__image--primary${cropClass} aspect-${aspect}`);
        image.alt = decodeEntities(product.alt) || decodeEntities(product.title);
        image.loading = "lazy";
        image.decoding = "async";
        const width = Number(product.image_width);
        const height = Number(product.image_height);
        if (Number.isSafeInteger(width) && width > 0 && Number.isSafeInteger(height) && height > 0) {
          const source = new URL(product.image, location.origin);
          const widths = [200, 300, 400, 500, 600, 800, 1000, 1200, 1600, 1800].filter((size) => size < width);
          widths.push(Math.min(width, 1800));
          image.sizes = this.imageSizes || "auto, (max-width: 699px) 50vw, (max-width: 999px) 50vw, 33vw";
          image.srcset = [...new Set(widths)].map((size) => {
            const candidate = new URL(source);
            candidate.searchParams.set("width", String(size));
            return `${candidate.href} ${size}w`;
          }).join(", ");
          image.width = width;
          image.height = height;
        } else {
          // Old cached index responses retain their working 600px fallback.
          image.width = 600;
          image.height = 600;
        }
        image.src = product.image;
        media.append(image);
        figure.append(media);
      }

      const info = el("div", "product-card__info");
      item.append(info);
      const stack = el("div", "v-stack justify-items-center gap-2");
      info.append(stack);

      if (showVendor && product.vendor) {
        stack.append(el("p", `smallcaps${textFont ? " heading" : ""}`, decodeEntities(product.vendor)));
      }

      const titleWrap = el("div", "v-stack justify-items-center gap-1");
      stack.append(titleWrap);

      const titleClass = ["product-title", textFont, maxLines > 0 ? "line-clamp" : ""].filter(Boolean).join(" ");
      const titleLink = link(titleClass, decodeEntities(product.display));
      titleLink.setAttribute("data-instant", "");
      if (maxLines > 0) titleLink.style.setProperty("--line-clamp-count", String(maxLines));
      titleWrap.append(titleLink);

      const action = link("ppa-card-action", product.sold_tag === true ? "View work" : product.inquiry === "true" ? (listedPrice(product) === null ? "Request price" : "Inquire") : "View work");
      const arrow = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      for (const [key, value] of Object.entries({class: "ppa-icon-arrow", width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", "stroke-width": "1.5", "stroke-linecap": "round", "stroke-linejoin": "round", "aria-hidden": "true", focusable: "false"})) arrow.setAttribute(key, value);
      const arrowPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
      arrowPath.setAttribute("d", "M3 13 13 3M3 3h10v10");
      arrow.append(arrowPath); action.append(" ", arrow); titleWrap.append(action);

      if (product.medium) titleWrap.append(el("p", "ppa-card-detail text-subdued", product.medium));

      const priceList = document.createElement("price-list");
      priceList.className = "price-list";
      const salePrice = document.createElement("sale-price");
      salePrice.className = textFont ? `${textFont} text-subdued` : "text-subdued";
      // Screen readers hear "Price" only before an amount; "SOLD" and
      // "Price on request" speak for themselves (and nothing here is on sale).
      if (/^\$/.test(product.label)) salePrice.append(el("span", "sr-only", "Price "));
      salePrice.append(document.createTextNode(product.label));
      priceList.append(salePrice);
      titleWrap.append(priceList);

      return item;
    }

    render(requestedPage) {
      const main = document.querySelector(".collection__main");
      if (!main) return;
      const size = Math.max(1, Number(this.dataset.pageSize) || 48);
      const pages = Math.max(1, Math.ceil(this.filtered.length / size));
      const page = Math.max(1, Math.min(pages, Math.floor(requestedPage)));

      const oldGrid = main.querySelector("product-list.product-list");
      const grid = document.createElement("product-list");
      grid.className = "product-list";
      grid.id = oldGrid?.id || "product-list-ppa-browse";
      grid.setAttribute("collection-mobile-layout", oldGrid?.getAttribute("collection-mobile-layout") || "medium");
      grid.setAttribute("collection-desktop-layout", oldGrid?.getAttribute("collection-desktop-layout") || "medium");
      const mobileSize = grid.getAttribute("collection-mobile-layout") === "large"
        ? "calc(100vw - 40px - 24px)"
        : "calc((100vw - 40px - 16px) / 2 - 24px)";
      this.imageSizes = `auto, (max-width: 640px) ${mobileSize}, (max-width: 999px) 50vw, 33vw`;
      for (const product of this.filtered.slice((page - 1) * size, page * size)) {
        grid.append(this.card(product));
      }
      // After a failed load, the error message stands where the grid was.
      const oldError = main.querySelector(".ppa-browse__error");
      if (oldGrid) oldGrid.replaceWith(grid);
      else if (oldError) oldError.replaceWith(grid);
      else main.append(grid);
      main.querySelectorAll(".ppa-browse__error").forEach((node) => node.remove());

      const oldPager = main.querySelector(".ppa-browse__pager, nav.pagination");
      oldPager?.remove();
      const oldEmpty = main.querySelector(".ppa-browse__empty");
      oldEmpty?.remove();

      this.setResultCount(this.filtered.length);
      this.status.textContent = `${this.filtered.length.toLocaleString()} ${this.filtered.length === 1 ? "work" : "works"}${this.filtered.length ? ` · Page ${page} of ${pages}` : " match these filters"}.`;

      if (this.filtered.length === 0) {
        const empty = document.createElement("p");
        empty.className = "ppa-browse__empty empty-state";
        empty.textContent = "No artwork matches these filters. Try another budget or artist.";
        main.append(empty);
        return;
      }

      if (pages > 1) {
        const nav = document.createElement("nav");
        nav.className = "ppa-browse__pager";
        nav.setAttribute("aria-label", "Artwork result pages");
        for (const [label, step] of [
          ["Previous", -1],
          ["Next", 1],
        ]) {
          const button = document.createElement("button");
          button.type = "button";
          button.className = "link";
          button.textContent = label;
          button.disabled = page + step < 1 || page + step > pages;
          button.addEventListener("click", () => {
            const url = new URL(location.href);
            url.searchParams.set("ppa_page", String(page + step));
            history.pushState({}, "", url);
            this.render(page + step);
            this.scrollIntoView({ block: "start" });
          });
          nav.append(button);
        }
        main.append(nav);
      }
    }
  }

  customElements.define("ppa-browse", PPABrowse);
})();
